import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'

import {
  listUserFeedback,
  type FeedbackStatus,
  type FeedbackType,
  type UserFeedbackRow,
} from '@/api/user_feedback'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { dateFnsLocale } from '@/utils/dateLocale'

const PAGE_SIZE = 50

type TypeFilter = FeedbackType | 'all'

/**
 * /user-feedback — список обращений от пользователей мобильного приложения.
 *
 * Состояние UI: status (open|resolved) + type-фильтр (all|bug|idea). Открытие
 * элемента ведёт на /user-feedback/:id.
 *
 * Источник данных: `public.user_feedback` (см. миграцию
 * `20260518120000_user_feedback.sql` в репо `stryzh`).
 */
export function UserFeedbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<FeedbackStatus>('open')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-feedback', status, typeFilter],
    queryFn: () =>
      listUserFeedback({
        status,
        type: typeFilter,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 15_000,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeader
        title={t('feedback.title')}
        subtitle={t('feedback.subtitle')}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedControl<FeedbackStatus>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: t('feedback.statusOpen') },
            { value: 'resolved', label: t('feedback.statusResolved') },
          ]}
          aria-label={t('feedback.statusFilterLabel')}
          className="!w-auto shrink-0 min-w-[280px]"
        />
        <SegmentedControl<TypeFilter>
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { value: 'all', label: t('feedback.typeAll') },
            { value: 'bug', label: t('feedback.typeBugs') },
            { value: 'idea', label: t('feedback.typeIdeas') },
          ]}
          aria-label={t('feedback.typeFilterLabel')}
          className="!w-auto shrink-0 min-w-[280px]"
        />
        <div className="ml-auto text-xs text-[color:var(--stryzh-color-text-secondary)]">
          {t('feedback.total', { count: total })}
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Banner kind="error" text={t('feedback.loadError', { message: (error as Error).message })} />
      ) : items.length === 0 ? (
        <Banner
          kind="muted"
          text={
            status === 'open'
              ? t('feedback.emptyOpen')
              : t('feedback.emptyResolved')
          }
        />
      ) : (
        <FeedbackTable
          rows={items}
          onOpen={(id) => navigate(`/user-feedback/${id}`)}
        />
      )}
    </div>
  )
}

function FeedbackTable({
  rows,
  onOpen,
}: {
  rows: UserFeedbackRow[]
  onOpen: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]">
      <table className="w-full text-sm">
        <thead className="border-b border-[color:var(--stryzh-color-border-default)] text-left text-xs uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2">{t('feedback.colType')}</th>
            <th className="px-3 py-2">{t('feedback.colWhen')}</th>
            <th className="px-3 py-2">{t('feedback.colUser')}</th>
            <th className="px-3 py-2">{t('feedback.colVersion')}</th>
            <th className="px-3 py-2">{t('feedback.colPlatform')}</th>
            <th className="px-3 py-2">{t('feedback.colScreen')}</th>
            <th className="px-3 py-2">{t('feedback.colDescription')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <FeedbackRow key={r.id} row={r} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FeedbackRow({
  row,
  onOpen,
}: {
  row: UserFeedbackRow
  onOpen: (id: string) => void
}) {
  const dateStr = useMemo(
    () => format(new Date(row.created_at), 'd MMM HH:mm', { locale: dateFnsLocale() }),
    [row.created_at],
  )
  const preview = row.description.length > 70
    ? row.description.slice(0, 70) + '…'
    : row.description
  const userLabel = row.user_full_name?.trim() || row.user_phone || row.user_id || '—'

  return (
    <tr
      onClick={() => onOpen(row.id)}
      className="cursor-pointer border-b border-[color:var(--stryzh-color-border-subtle)] transition hover:bg-white/5"
    >
      <td className="px-3 py-2">
        <TypeBadge type={row.type} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-[color:var(--stryzh-color-text-secondary)]">
        {dateStr}
      </td>
      <td className="px-3 py-2 max-w-[200px] truncate">{userLabel}</td>
      <td className="px-3 py-2 whitespace-nowrap text-[color:var(--stryzh-color-text-secondary)]">
        {row.app_version ?? '—'}
        {row.build_number != null ? ` (${row.build_number})` : ''}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-[color:var(--stryzh-color-text-secondary)]">
        {row.platform ?? '—'}
      </td>
      <td className="px-3 py-2 max-w-[180px] truncate text-[color:var(--stryzh-color-text-secondary)]">
        {row.route ?? '—'}
      </td>
      <td className="px-3 py-2 max-w-[420px] truncate">{preview}</td>
    </tr>
  )
}

export function TypeBadge({ type }: { type: FeedbackType }) {
  const { t } = useTranslation()
  const isBug = type === 'bug'
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ' +
        (isBug
          ? 'bg-red-500/10 text-red-300 border border-red-500/30'
          : 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30')
      }
    >
      <span aria-hidden>{isBug ? '🐞' : '💡'}</span>
      {isBug ? t('feedback.typeBug') : t('feedback.typeIdea')}
    </span>
  )
}

function Banner({ kind, text }: { kind: 'error' | 'muted'; text: string }) {
  const cls =
    kind === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
